import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

import OverlayMotion from '@/components/motion/OverlayMotion'
import { cn } from '@/lib/utils'

/** Air between the trigger and the panel, and between the panel and the viewport edge. */
const GAP = 8
const EDGE = 8

/**
 * A panel squeezed below this is useless, so it is allowed to run past the
 * fold and scroll instead. Only reachable when the trigger itself is jammed
 * against an edge.
 */
const MIN_PANEL_H = 140

interface Placement {
  top: number
  left: number
  maxHeight: number
  /** Opened upward, so the entrance grows from the bottom edge. */
  above: boolean
}

/**
 * Where `top: 0; left: 0` lands in the viewport for this panel — the origin of
 * its containing block. Read off the panel's own box, not reasoned from the
 * ancestors' styles: whatever made the containing block (a `translate` on a
 * dialog, a `filter`, `contain`) the difference between where the panel is and
 * where its `top`/`left` say it is, is the offset. Zero under `<body>` and in
 * the top layer. A `scale` mid-animation is the one thing a translation cannot
 * express, and it is 2% for 200ms.
 */
function containingOrigin(panel: HTMLElement) {
  const box = panel.getBoundingClientRect()
  // React writes numbers as `<n>px`; anything else has not been placed yet.
  const px = (value: string) => (value.endsWith('px') ? Number(value.slice(0, -2)) : 0)
  return { x: box.left - px(panel.style.left), y: box.top - px(panel.style.top) }
}

/**
 * `side="right"`, in viewport coordinates: beside the anchor instead of under
 * it, top edges lined up (bottom edges for `align="end"`), slid up only as far
 * as the viewport's bottom edge demands. It never flips to the left — it is
 * meant for a trigger at the page's left edge, a sidebar row — but
 * it is still clamped, so a narrow window overlaps rather than overflows.
 */
function besideAnchor(
  rect: DOMRect,
  wanted: number,
  width: number,
  align: 'start' | 'end',
  offset: number,
) {
  const maxHeight = Math.max(window.innerHeight - 2 * EDGE, MIN_PANEL_H)
  const height = Math.min(wanted, maxHeight)
  const wishTop = align === 'end' ? rect.bottom - height : rect.top
  const top = Math.max(EDGE, Math.min(wishTop, window.innerHeight - EDGE - height))
  const rightmost = Math.max(EDGE, window.innerWidth - EDGE - width)
  const left = Math.min(Math.max(rect.right + offset, EDGE), rightmost)
  return { top, left, maxHeight }
}

const MENU_ITEMS = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'

/**
 * What a `hasPopup="menu"` panel roves between: its enabled items, in DOM
 * order. Read off the DOM when a key lands rather than registered by the
 * caller, so a menu is just children carrying the menu roles — the markup
 * RowMenu and PortfolioOptionsMenu each hand-rolled a roving loop for.
 */
function enabledMenuItems(panel: HTMLElement | null): HTMLElement[] {
  if (panel === null) return []
  return Array.from(panel.querySelectorAll<HTMLElement>(MENU_ITEMS)).filter(
    (item) => item.getAttribute('aria-disabled') !== 'true' && !item.matches(':disabled'),
  )
}

/** What Tab can land on inside a panel. `tabindex="-1"` rows are the arrows' job. */
const TABBABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled):not([type="hidden"])',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function tabbables(panel: HTMLElement | null): HTMLElement[] {
  if (panel === null) return []
  return Array.from(panel.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (node) => node.tabIndex >= 0 && node.closest('[hidden], [inert]') === null,
  )
}

export interface PopoverTriggerProps {
  'aria-expanded': boolean
  'aria-controls': string | undefined
  'aria-haspopup': 'listbox' | 'dialog' | 'menu'
  onClick: () => void
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
}

interface PopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Renders the anchor. Spread the supplied props onto it, and return exactly
   * ONE element — it is the box the panel measures itself against.
   */
  trigger: (props: PopoverTriggerProps) => ReactNode
  /**
   * Focused when the panel opens — the search field, normally. Falls back to
   * the panel, which is a poor landing spot, so pass one.
   */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Which trigger edge the panel lines up with, before clamping overrides it. */
  align?: 'start' | 'end'
  /**
   * Which side of the trigger the panel opens on. `bottom` (the default)
   * flips above when there is more room there; `right` opens beside the
   * trigger — a flyout off a sidebar row — and `align` then lines up the top
   * (`start`) or bottom (`end`) edges instead of the left or right ones.
   */
  side?: 'bottom' | 'right'
  /**
   * `above` (rest:al) opens upward whenever the room above fits the panel, as the Alerts dialog's
   * Expires menu does (mock `bottom: 100% + 4`), so it never covers the fields below it. Default
   * `auto`: below, flipping only when there is more room above. Ignored for `side="right"`.
   */
  prefer?: 'auto' | 'above'
  /**
   * Air between the trigger and a `side="right"` panel. The sidebar's rows
   * stop short of the rail's own edge, and the flyout is drawn a set distance
   * off that edge, not off the row. Below the trigger keeps its fixed gap.
   */
  sideOffset?: number
  /**
   * What the trigger announces. `menu` also makes the panel the `role="menu"`
   * and gives it the APG menu keys: the arrows, Home and End rove over the
   * enabled `menuitem*` children, Tab leaves and closes it, and it opens onto
   * the first enabled item. Items keep `tabIndex={-1}`; the arrows are how a
   * member moves between them.
   */
  hasPopup?: 'listbox' | 'dialog' | 'menu'
  /**
   * Names a `menu` or `dialog` panel for assistive tech. Ignored for `listbox`, whose callers put
   * `role="listbox"` and its name on their own list, below a search field that is not an option.
   */
  label?: string
  /**
   * The children render their own `role="dialog"` with its own name (PeriodPicker's titled
   * calendar), so the panel stays a plain box rather than nesting a dialog in a dialog.
   */
  childOwnsDialog?: boolean
  /** Panel width and nothing else — height is measured, not declared. */
  className?: string
  children: ReactNode
}

/**
 * The anchored overlay every menu in the system sits on.
 *
 * Portalled to `<body>` and positioned `fixed`. Both halves of that are
 * load-bearing: these menus open from filter rows that sit inside tables and
 * cards with `overflow` clipping, so an absolutely-positioned panel would be
 * cut off; and once the panel is out of the flow, `fixed` coordinates ARE
 * viewport coordinates, which makes clamping arithmetic instead of a library.
 *
 * Dismissal listens for `pointerdown`, not `click`. A member who starts a drag
 * on a row and releases past the panel edge fires a `click` whose target is
 * outside — closing there would eat the gesture. A `pointerdown` can only land
 * outside if the press itself started outside.
 *
 * INSIDE A MODAL `<dialog>` the portal target changes. `showModal()` puts the
 * dialog in the top layer and makes everything outside it inert, so a panel
 * portalled to `<body>` would paint under the dialog and refuse every click
 * and focus — HTML's rule, not an engine's quirk. The panel is portalled into
 * the nearest open dialog that holds the anchor instead, and promoted into the
 * top layer itself as a `popover="manual"`: that escapes the dialog's own
 * `overflow` clipping, and a top-layer box's containing block is the viewport,
 * so the dialog's `translate`/`scale` (always set — see Modal) cannot shift
 * it. Where the Popover API is missing the panel stays an ordinary `fixed`
 * child of the transformed dialog, and `place` converts viewport coordinates
 * into whatever containing block it actually got — see `containingOrigin`.
 *
 * The trigger is wrapped in a `display: contents` span rather than handed a
 * ref. The wrapper draws no box and so changes no layout, and it lets the
 * anchor be found in the DOM instead of passing a ref through a function call
 * during render, which React's rules of refs forbid.
 */
export default function Popover({
  open,
  onOpenChange,
  trigger,
  initialFocusRef,
  align = 'start',
  side = 'bottom',
  prefer = 'auto',
  sideOffset = GAP,
  hasPopup = 'listbox',
  label,
  childOwnsDialog = false,
  className,
  children,
}: PopoverProps) {
  const panelRole =
    hasPopup === 'menu' ? 'menu' : hasPopup === 'dialog' && !childOwnsDialog ? 'dialog' : undefined
  const panelId = useId()
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)

  /**
   * The dialog the anchor sits in, found once when the anchor mounts. Kept in
   * state rather than read off the ref during render, which the rules of refs
   * forbid; whether it is open is checked when the portal target is chosen.
   */
  const [dialogHost, setDialogHost] = useState<HTMLDialogElement | null>(null)
  const bindAnchor = useCallback((node: HTMLSpanElement | null) => {
    anchorRef.current = node
    setDialogHost(node?.closest('dialog') ?? null)
  }, [])
  const host = dialogHost?.open === true ? dialogHost : null
  const inDialog = host !== null

  /** The panel's height with no cap applied — see `measure`. */
  const naturalHeight = useRef(0)

  const anchorNode = useCallback(() => {
    const node = anchorRef.current?.firstElementChild
    return node instanceof HTMLElement ? node : null
  }, [])

  /**
   * Lifts the cap, reads the height, puts the cap back — synchronously, so
   * nothing is ever painted in between.
   *
   * Reading the height with `maxHeight` still on it measures the cap rather
   * than the content, and every later pass would then shrink to fit the
   * previous one until the panel collapsed. Scroll and resize reuse the cached
   * figure instead of repeating this: forcing two reflows per scroll frame
   * over a table of thousands of rows is not worth a number that has not
   * changed.
   */
  const measure = useCallback(() => {
    const panel = panelRef.current
    if (!panel) return
    const capped = panel.style.maxHeight
    panel.style.maxHeight = ''
    naturalHeight.current = panel.offsetHeight
    panel.style.maxHeight = capped
  }, [])

  const place = useCallback(() => {
    const anchor = anchorNode()
    const panel = panelRef.current
    if (!anchor || !panel) return

    const rect = anchor.getBoundingClientRect()
    const wanted = naturalHeight.current

    if (side === 'right') {
      const beside = besideAnchor(rect, wanted, panel.offsetWidth, align, sideOffset)
      // Same containing-block correction and same bail-out as below.
      const origin = containingOrigin(panel)
      const top = beside.top - origin.y
      const left = beside.left - origin.x
      setPlacement((previous) =>
        previous &&
        previous.top === top &&
        previous.left === left &&
        previous.maxHeight === beside.maxHeight
          ? previous
          : { top, left, maxHeight: beside.maxHeight, above: false },
      )
      return
    }

    const roomBelow = window.innerHeight - rect.bottom - GAP - EDGE
    const roomAbove = rect.top - GAP - EDGE
    const flip =
      prefer === 'above'
        ? roomAbove >= wanted || roomAbove > roomBelow
        : wanted > roomBelow && roomAbove > roomBelow

    const maxHeight = Math.max(flip ? roomAbove : roomBelow, MIN_PANEL_H)
    const height = Math.min(wanted, maxHeight)
    const viewportTop = flip ? Math.max(EDGE, rect.top - GAP - height) : rect.bottom + GAP

    const width = panel.offsetWidth
    const wish = align === 'end' ? rect.right - width : rect.left
    const rightmost = Math.max(EDGE, window.innerWidth - EDGE - width)

    // Everything above is viewport coordinates; `top`/`left` are resolved
    // against the containing block, which is not the viewport when a
    // transformed dialog holds the panel and the top layer is unavailable.
    const origin = containingOrigin(panel)
    const top = viewportTop - origin.y
    const left = Math.min(Math.max(wish, EDGE), rightmost) - origin.x

    // Bailing out on an unchanged result is what stops the ResizeObserver
    // below from chasing its own `maxHeight` round in a loop.
    setPlacement((previous) =>
      previous &&
      previous.top === top &&
      previous.left === left &&
      previous.maxHeight === maxHeight &&
      previous.above === flip
        ? previous
        : { top, left, maxHeight, above: flip },
    )
  }, [align, anchorNode, prefer, side, sideOffset])

  useLayoutEffect(() => {
    if (!open) return

    // Into the top layer before measuring, so the first read is taken in the
    // containing block the panel will actually be painted in.
    const shown = panelRef.current
    if (
      inDialog &&
      shown &&
      typeof shown.showPopover === 'function' &&
      !shown.matches(':popover-open')
    ) {
      shown.showPopover()
    }

    // The last placement is deliberately left in state. A layout effect runs
    // before the browser paints, so a reopened panel is re-measured and moved
    // while the stale coordinates are still off-screen.
    measure()
    place()

    // Capture phase because `scroll` does not bubble: a listener on window
    // only hears an inner scroller's scroll on the way down.
    window.addEventListener('scroll', place, { capture: true, passive: true })
    window.addEventListener('resize', place)

    // The panel shrinks as a search narrows the list, and a shorter panel may
    // now fit below a trigger it had to flip above.
    const observer = new ResizeObserver(() => {
      measure()
      place()
    })
    const panel = panelRef.current
    if (panel) observer.observe(panel)

    return () => {
      window.removeEventListener('scroll', place, { capture: true })
      window.removeEventListener('resize', place)
      observer.disconnect()
    }
  }, [open, inDialog, measure, place])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (panelRef.current?.contains(target) === true) return
      if (anchorRef.current?.contains(target) === true) return
      onOpenChange(false)
    }

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Stop here rather than letting a surrounding sheet or dialog close too.
      // Both halves are needed: `stopPropagation` keeps useModalDialog's own
      // keydown listener from hearing the key, and `preventDefault` cancels
      // the keydown's default action — the close request a modal `<dialog>`
      // answers natively with `cancel`, whatever the listeners do.
      event.preventDefault()
      event.stopPropagation()
      onOpenChange(false)
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, onOpenChange])

  /**
   * Focus waits for the first placement because an unplaced panel is drawn
   * `visibility: hidden` (see the style below) and `focus()` on anything inside
   * a hidden subtree is silently a no-op — no error, no move, focus left on
   * `<body>`. It really does land in that window: React flushes this passive
   * effect BEFORE the synchronous re-render that `setPlacement` schedules from
   * the layout effect above, so on a popover's first open the panel is still
   * hidden when the call is made. Measured, not reasoned: `focus()` was called
   * on the search field with `getComputedStyle().visibility === 'hidden'` and
   * `document.activeElement` stayed `BODY` at 0/50/150/300/600ms.
   *
   * A reopen keeps the previous placement, so this is already true on entry
   * and the effect still runs exactly once per open. Scroll and resize replace
   * the placement object but not its existence, which is why the flag is a
   * boolean and not `placement` itself — depending on the object would drag
   * focus back out of the list on every scroll frame.
   */
  const placed = placement !== null

  useEffect(() => {
    if (!open || !placed) return
    const menuLanding = hasPopup === 'menu' ? enabledMenuItems(panelRef.current)[0] : undefined
    const landing = initialFocusRef?.current ?? menuLanding ?? panelRef.current
    landing?.focus()

    return () => {
      // Removing the focused node drops focus to <body>, and a keyboard member
      // left on <body> has to tab from the top of the document again. Anything
      // else already holds focus deliberately — a control the member clicked
      // outside the panel — so it is left alone.
      if (document.activeElement === document.body) anchorNode()?.focus()
    }
  }, [open, placed, initialFocusRef, anchorNode, hasPopup])

  /**
   * Tab and Shift+Tab wrap inside a `listbox` or `dialog` panel. The panel is
   * portalled to the end of `<body>` (or of a dialog), so Tab off its last
   * control would otherwise leave the page for the browser's chrome, and
   * Shift+Tab off its first would land on whatever sits last in the document
   * — nowhere near the trigger. Escape and an outside press still close it;
   * a menu instead closes on Tab (APG), below.
   */
  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const stops = tabbables(panelRef.current)
    const first = stops[0]
    const last = stops.at(-1)
    if (first === undefined || last === undefined) {
      event.preventDefault()
      return
    }
    const current = document.activeElement
    if (event.shiftKey && (current === first || current === panelRef.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && current === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Tab leaves the menu and closes it, keeping its default: the close hands
    // focus back to the trigger first, so the key moves on from there rather
    // than from the end of <body>, where the panel is portalled.
    if (event.key === 'Tab') {
      onOpenChange(false)
      return
    }
    const items = enabledMenuItems(panelRef.current)
    const last = items.length - 1
    if (last < 0) return
    const current = items.findIndex((item) => item === document.activeElement)
    let next: number
    switch (event.key) {
      case 'ArrowDown':
        next = current === last ? 0 : current + 1
        break
      case 'ArrowUp':
        next = current <= 0 ? last : current - 1
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = last
        break
      default:
        return
    }
    event.preventDefault()
    items[next]?.focus()
  }

  const triggerProps: PopoverTriggerProps = {
    'aria-expanded': open,
    // Pointing at an id that is not in the document is worse than saying
    // nothing, and the panel only exists while open.
    'aria-controls': open ? panelId : undefined,
    'aria-haspopup': hasPopup,
    onClick: () => {
      onOpenChange(!open)
    },
    onKeyDown: (event) => {
      // A panel that opens to the right answers the arrow that points at it.
      if (!open && side === 'right' && event.key === 'ArrowRight') {
        event.preventDefault()
        onOpenChange(true)
        return
      }
      if (open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return
      event.preventDefault()
      onOpenChange(true)
    },
  }

  return (
    <>
      <span ref={bindAnchor} className="contents">
        {trigger(triggerProps)}
      </span>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              tabIndex={-1}
              role={panelRole}
              aria-label={panelRole === undefined ? undefined : label}
              onKeyDown={hasPopup === 'menu' ? onMenuKeyDown : onPanelKeyDown}
              // Top layer inside a modal dialog; see the component note.
              popover={inDialog ? 'manual' : undefined}
              style={{
                top: placement?.top ?? 0,
                left: placement?.left ?? 0,
                maxHeight: placement?.maxHeight,
                // One frame at (0,0) before the first measurement lands would
                // read as a flash in the corner of the screen.
                visibility: placement ? undefined : 'hidden',
              }}
              className={cn(
                // `inset-auto m-0 overflow-visible` undo the UA's `[popover]`
                // centring and scrolling; inert outside a dialog.
                'fixed inset-auto z-50 m-0 flex flex-col overflow-visible gap-1 rounded-md border border-border-subtle bg-popover p-2 text-popover-foreground shadow-(--shadow-nav) focus:outline-none',
                // Its position must never transition. Under reduced motion
                // globals.css gives EVERY element a 0.01ms transition, and the
                // default property is `all`: `top`/`left` then trail the style
                // by a frame, the second `place()` reads the box at its old
                // spot, and `containingOrigin` doubles the coordinates — every
                // menu opened off-screen on its first open. Measured in Chrome.
                'transition-none',
                className,
              )}
            >
              {/* The entrance lives here, not on the panel: a transform on the panel
                  would skew the measurements `place` takes of it. It grows from the
                  edge nearest the trigger. */}
              <OverlayMotion
                kind="menu"
                className="flex flex-col gap-1"
                style={{
                  transformOrigin: `${align === 'end' ? 'right' : 'left'} ${placement?.above === true ? 'bottom' : 'top'}`,
                }}
              >
                {children}
              </OverlayMotion>
            </div>,
            host ?? document.body,
          )
        : null}
    </>
  )
}
